import type { ThreadedPostInput } from './graphqlTypes'
import { AttachmentTypes } from '~publish/legacy/constants'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import {
  ensureLinkAttachmentProtocol,
  mapMediaAssets,
  toLinkAttachmentInput,
} from './mapAssets'

/**
 * Maps a threaded draft to the full ordered `ThreadedPostInput[]` (root at index
 * 0), each entry's own `text` + `assets` (+ `metadata` when the entry carries a
 * link attachment). Returns `undefined` for a non-threaded draft so callers omit
 * the `thread` key.
 *
 * `draft.thread` is the source of truth: the active post's live editor content is
 * committed into it by the save-time flush (see `useGraphCallbacks`), and every
 * other post was committed when it lost focus.
 */
export function mapThread(draft: Draft): ThreadedPostInput[] | undefined {
  if (!draft.hasThread()) return undefined

  const canTag = draft.service.canHaveUserTags
  const serviceName = draft.service.name

  // Thread-item metadata exists only for Threads and Bluesky. `thread` is
  // replaced wholesale on save, so an entry without a link needs no clearing.
  return draft.thread.map((post) => {
    const linkAttachment =
      post.enabledAttachmentType === AttachmentTypes.LINK && post.link !== null
        ? toLinkAttachmentInput(post.link)
        : undefined
    if (
      linkAttachment !== undefined &&
      (serviceName === 'threads' || serviceName === 'bluesky')
    ) {
      return {
        text: post.text,
        assets: [],
        metadata: {
          [serviceName]: {
            linkAttachment:
              serviceName === 'bluesky'
                ? ensureLinkAttachmentProtocol(linkAttachment)
                : linkAttachment,
          },
        },
      }
    }
    return { text: post.text, assets: mapMediaAssets(post, { canTag }) }
  })
}
